export type OwnerType = 'individual' | 'company';

type OwnerRegistrationInput = {
  email: string;
  password: string;
  full_name: string;
  phone_number: string;
  language: string;
  owner_type: OwnerType;
  also_driver_intent: 'yes' | 'no' | null;
  ownerNif?: string;
  companyName?: string;
  companyNipc?: string;
  numeroLicenca?: string;
  /** D-057: optional referral code and its server-issued registration session. */
  referralCode?: string;
  referralToken?: string;
};

export type OwnerRegistrationPayload = {
  email: string;
  password: string;
  full_name: string;
  phone_number: string;
  language: string;
  owner_type: OwnerType;
  also_driver_intent: 'yes' | 'no' | null;
  referral_code?: string;
  referral_token?: string;
  nif?: string;
  company?: {
    name: string;
    nipc: string;
    numero_licenca: string;
  };
};

const digitsOnly = (value: string | undefined): string => (value ?? '').replace(/\D/g, '');

/**
 * Builds the register-owner request without mixing CRM fleet size with billing.
 * The backend contract keeps `fleet_size_range` (CRM) and `quantity` (licenses)
 * in later onboarding steps, never in this account-creation payload.
 */
export const buildOwnerRegistrationPayload = (
  input: OwnerRegistrationInput,
): OwnerRegistrationPayload => {
  const referral = (input.referralCode || input.referralToken)
    ? {
        ...(input.referralCode ? { referral_code: input.referralCode } : {}),
        ...(input.referralToken ? { referral_token: input.referralToken } : {}),
      }
    : {};
  const base = {
    email: input.email,
    password: input.password,
    full_name: input.full_name,
    phone_number: input.phone_number,
    language: input.language,
    owner_type: input.owner_type,
    also_driver_intent: input.also_driver_intent,
    ...referral,
  };

  if (input.owner_type === 'individual') {
    return {
      ...base,
      nif: digitsOnly(input.ownerNif),
    };
  }

  return {
    ...base,
    company: {
      name: input.companyName ?? '',
      nipc: digitsOnly(input.companyNipc),
      numero_licenca: input.numeroLicenca ?? '',
    },
  };
};
